import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

export default function FactorCalc() {
  const [n, setN] = useState(60);
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const num = Math.abs(Math.floor(n));
    if (num === 0) return { factors: [], primes: [], pairs: [] };
    const factors = [];
    for (let i = 1; i <= Math.sqrt(num); i++) if (num % i === 0) { factors.push(i); if (i !== num/i) factors.push(num/i); }
    factors.sort((a, b) => a - b);
    // Prime factorization
    let x = num; const primes = [];
    for (let i = 2; i <= x; i++) while (x % i === 0) { primes.push(i); x /= i; }
    // Factor pairs — unique (a, b) with a ≤ b and a·b = num
    const pairs = [];
    for (let i = 1; i <= Math.sqrt(num); i++) if (num % i === 0) pairs.push([i, num/i]);
    // Group primes exponents
    const expGroups = {};
    primes.forEach(p => { expGroups[p] = (expGroups[p] || 0) + 1; });
    const primeExpDisplay = Object.entries(expGroups).map(([p, e]) => e > 1 ? `${p}^${e}` : p).join(' × ');
    return { factors, primes, pairs, primeExpDisplay };
  }, [n]);
  return (
    <CalcShell look="cnet" title="Factor Calculator" subtitle="List all factors, factor pairs, and the prime factorization of a positive integer.">
      <div className="field"><label>Number</label><input type="number" value={n} onChange={e => setN(+e.target.value || 0)} data-testid="fac-n"/></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">All factors ({c.factors.length})</h3>
          <div className="flex flex-wrap gap-2" data-testid="fac-list">
            {c.factors.map(f => <span key={f} className="px-3 py-1 bg-white rounded-lg font-mono text-blue-600 border border-slate-200">{f}</span>)}
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">Prime factorization</h3>
          <div className="font-mono text-lg" data-testid="fac-primes">{c.primes.length ? c.primes.join(' × ') : '—'}</div>
          {c.primes.length > 0 && <div className="text-sm mt-2 text-slate-600">Exponent form: <b className="font-mono">{c.primeExpDisplay}</b></div>}
        </div>
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Factor Pairs ({c.pairs.length})</h3>
        <table className="w-full text-sm" data-testid="fac-pairs">
          <thead className="bg-blue-800 text-white">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-right">a</th>
              <th className="px-3 py-2 text-center">×</th>
              <th className="px-3 py-2 text-left">b</th>
              <th className="px-3 py-2 text-right">= {n}</th>
            </tr>
          </thead>
          <tbody>
            {c.pairs.map(([a, b], i) => (
              <tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}>
                <td className="px-3 py-2">{i + 1}</td>
                <td className="px-3 py-2 text-right font-mono">{a}</td>
                <td className="px-3 py-2 text-center">×</td>
                <td className="px-3 py-2 font-mono">{b}</td>
                <td className="px-3 py-2 text-right text-slate-500">= {a * b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="fac-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="fac-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Trial division</b>: try each integer from 1 up to √n. If it divides n exactly, both it and n / it are factors.</li>
              <li><b>Prime factorization</b>: repeatedly divide n by the smallest prime that divides it (2, 3, 5, 7, …) until n becomes 1. The primes collected form the factorization: <span className="font-mono">{n} = {c.primes.join(' × ') || n}</span></li>
              <li><b>Exponent form</b>: group repeated primes → <span className="font-mono">{c.primeExpDisplay}</span></li>
              <li><b>Factor pairs</b>: {c.pairs.length} pairs (a, b) where a ≤ b and a·b = {n}.</li>
              <li><b>Number of factors</b>: {c.factors.length}. (For any n = p₁^a · p₂^b · …, the count is (a+1)(b+1)·…)</li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
